import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Eye, CheckCircle, XCircle, UserPlus, Building2, FileText, ChevronDown, ChevronUp, PauseCircle, Trash2, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getAuthPhotoUrl } from '../components/AuthImage';

const GestionStagiaires = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [tuteurs, setTuteurs] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [stagiaireDocuments, setStagiaireDocuments] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [stagRes, tutRes, entRes] = await Promise.all([
        axios.get('/stagiaires'),
        axios.get('/tuteurs'),
        axios.get('/entreprises')
      ]);
      setStagiaires(stagRes.data);
      setTuteurs(tutRes.data);
      setEntreprises(entRes.data);
    } catch (error) {
    }
  };

  const loadStagiaireDocuments = async (stagiaireId) => {
    try {
      const response = await axios.get(`/documents/${stagiaireId}`);
      setStagiaireDocuments(prev => ({ ...prev, [stagiaireId]: response.data }));
    } catch (error) {
    }
  };

  const handleAccepter = async (stagiaireId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/statut`, { statut_validation: 'accepte', accepte: true });
      toast.success('Stagiaire accepté - Accès activé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation');
    }
  };

  const handleMettreEnVeille = async (stagiaireId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/statut`, { statut_validation: 'veille' });
      toast.success('Stagiaire mis en veille - Accès désactivé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise en veille');
    }
  };

  const handleSupprimer = async (stagiaireId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce stagiaire ? Cette action supprimera tous ses documents et données.')) {
      return;
    }
    try {
      await axios.put(`/stagiaires/${stagiaireId}/statut`, { statut_validation: 'supprime' });
      toast.success('Stagiaire supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAttribuerTuteur = async (stagiaireId, tuteurId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/attribuer-tuteur`, { tuteur_id: tuteurId });
      toast.success('Tuteur attribué');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleAttribuerEntreprise = async (stagiaireId, entrepriseId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/attribuer-entreprise`, { entreprise_id: entrepriseId });
      toast.success('Entreprise attribuée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleAttribuerTuteur2 = async (stagiaireId, tuteurId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/attribuer-tuteur-2`, { tuteur_id: tuteurId });
      toast.success('Tuteur 2 attribué');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleAttribuerEntreprise2 = async (stagiaireId, entrepriseId) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/attribuer-entreprise-2`, { entreprise_id: entrepriseId });
      toast.success('Entreprise 2 attribuée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleUpdateStatut = async (stagiaireId, field, value) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}/statut`, { [field]: value });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleExpand = (stagiaireId) => {
    if (expandedStagiaire === stagiaireId) {
      setExpandedStagiaire(null);
    } else {
      setExpandedStagiaire(stagiaireId);
      if (!stagiaireDocuments[stagiaireId]) {
        loadStagiaireDocuments(stagiaireId);
      }
    }
  };

  const filteredStagiaires = stagiaires.filter((s) => {
    if (filter === 'all') return s.statut_validation !== 'supprime' && !s.archived;
    if (filter === 'atlantique') return s.centre_formation === 'Atlantique' && s.statut_validation !== 'supprime' && !s.archived;
    if (filter === 'occitanie') return s.centre_formation === 'Occitanie' && s.statut_validation !== 'supprime' && !s.archived;
    if (filter === 'stagiaire') return s.statut === 'stagiaire' && s.statut_validation !== 'supprime' && !s.archived;
    if (filter === 'apprenti') return s.statut === 'apprenti' && s.statut_validation !== 'supprime' && !s.archived;
    if (filter === 'en_attente') return (s.statut_validation === 'en_attente' || (!s.statut_validation && !s.accepte)) && !s.archived;
    if (filter === 'accepte') return (s.statut_validation === 'accepte' || s.accepte) && !s.archived;
    if (filter === 'veille') return s.statut_validation === 'veille';
    if (filter === 'supprime') return s.statut_validation === 'supprime';
    if (filter === 'archives') return s.archived === true;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="gestion-stagiaires-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Gestion des Stagiaires</h1>
          <p className="text-slate-600">Accepter, attribuer tuteur/entreprise et gérer les inscriptions</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-6 py-3 shadow-sm">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-[#8DC63F]" />
            <div>
              <p className="text-2xl font-display font-bold text-[#0F172A]">{stagiaires.length}</p>
              <p className="text-xs text-slate-600">Stagiaires</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'en_attente', label: 'En attente' },
            { value: 'accepte', label: 'Acceptés' },
            { value: 'veille', label: 'En veille' },
            { value: 'supprime', label: 'Supprimés' },
            { value: 'atlantique', label: 'Atlantique' },
            { value: 'occitanie', label: 'Occitanie' },
            { value: 'archives', label: 'Archivés (Moniteurs)' }
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.value
                  ? 'bg-[#8DC63F] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stagiaires List */}
      <div className="space-y-4">
        {filteredStagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            {filter === 'archives' ? (
              <>
                <Archive className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun stagiaire archivé</p>
                <p className="text-sm text-slate-400 mt-1">Les stagiaires convertis en Moniteur VNM apparaîtront ici</p>
              </>
            ) : (
              <p className="text-slate-500">Aucun stagiaire trouvé</p>
            )}
          </div>
        ) : (
          filteredStagiaires.map((stagiaire) => (
            <div key={stagiaire.user_id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`stagiaire-card-${stagiaire.user_id}`}>
              {/* Header */}
              <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  {stagiaire.user_info?.photo_identite ? (
                    <img src={getAuthPhotoUrl(stagiaire.user_info.photo_identite)} alt={`${stagiaire.user_info?.prenom} ${stagiaire.user_info?.nom}`}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                      data-testid={`stagiaire-photo-${stagiaire.user_id}`}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center ${stagiaire.user_info?.photo_identite ? 'hidden' : ''}`}>
                    <span className="text-lg font-semibold text-[#8DC63F]">
                      {stagiaire.user_info?.prenom?.[0]}{stagiaire.user_info?.nom?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-lg">
                      {stagiaire.user_info?.prenom} {stagiaire.user_info?.nom}
                    </p>
                    <p className="text-sm text-slate-600">{stagiaire.user_info?.email}</p>
                    <p className="text-xs text-slate-500 mt-1">{stagiaire.centre_formation}</p>
                  </div>
                  {/* Badges de statut */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {stagiaire.archived ? (
                      <span className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200 flex items-center gap-1">
                        <Archive className="h-3 w-3" /> Converti en Moniteur
                      </span>
                    ) : stagiaire.statut_validation === 'accepte' || stagiaire.accepte ? (
                      <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Actif
                      </span>
                    ) : stagiaire.statut_validation === 'veille' ? (
                      <span className="px-3 py-1 bg-gray-50 text-gray-700 text-xs font-medium rounded-full border border-gray-200 flex items-center gap-1">
                        <PauseCircle className="h-3 w-3" /> En veille
                      </span>
                    ) : stagiaire.statut_validation === 'supprime' ? (
                      <span className="px-3 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200 flex items-center gap-1">
                        <XCircle className="h-3 w-3" /> Supprimé
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-medium rounded-full border border-orange-200">
                        En attente
                      </span>
                    )}
                    {stagiaire.new_docs_count > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full border border-blue-300 flex items-center gap-1" data-testid={`new-docs-badge-${stagiaire.user_id}`}>
                        <FileText className="h-3 w-3" />
                        {stagiaire.new_docs_count} nouveau{stagiaire.new_docs_count > 1 ? 'x' : ''} doc{stagiaire.new_docs_count > 1 ? 's' : ''}
                      </span>
                    )}
                    {stagiaire.total_docs > 0 && !stagiaire.new_docs_count && (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full border border-slate-200 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {stagiaire.total_docs} doc{stagiaire.total_docs > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* 3 boutons d'action */}
                <div className="flex flex-wrap items-center gap-2">
                  {!stagiaire.archived && stagiaire.statut_validation !== 'accepte' && !stagiaire.accepte && stagiaire.statut_validation !== 'supprime' && (
                    <Button onClick={() => handleAccepter(stagiaire.user_id)} className="bg-green-600 hover:bg-green-700" size="sm" data-testid={`accepter-${stagiaire.user_id}`}>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Accepter
                    </Button>
                  )}
                  {!stagiaire.archived && stagiaire.statut_validation !== 'veille' && stagiaire.statut_validation !== 'supprime' && (
                    <Button onClick={() => handleMettreEnVeille(stagiaire.user_id)} variant="outline" className="border-gray-300 text-gray-600" size="sm" data-testid={`veille-${stagiaire.user_id}`}>
                      <PauseCircle className="h-4 w-4 mr-1" />
                      En veille
                    </Button>
                  )}
                  {!stagiaire.archived && stagiaire.statut_validation !== 'supprime' && (
                    <Button onClick={() => handleSupprimer(stagiaire.user_id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" size="sm" data-testid={`supprimer-${stagiaire.user_id}`}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  )}
                  {stagiaire.archived && (
                    <Button variant="outline" size="sm" className="border-purple-300 text-purple-600" onClick={() => window.location.href = `/fiche-stagiaire/${stagiaire.user_id}`} data-testid={`voir-fiche-${stagiaire.user_id}`}>
                      <Eye className="h-4 w-4 mr-1" />
                      Voir la fiche
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => toggleExpand(stagiaire.user_id)} data-testid={`expand-${stagiaire.user_id}`}>
                    {expandedStagiaire === stagiaire.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Quick Info Bar - Ligne 1 */}
              {stagiaire.archived ? (
                <div className="px-4 pb-2">
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                    <p className="text-purple-700 font-medium">Stagiaire archivé — {stagiaire.archived_reason || 'Converti en moniteur VNM'}</p>
                    {stagiaire.archived_at && (
                      <p className="text-purple-600 text-xs mt-1">Archivé le {new Date(stagiaire.archived_at).toLocaleDateString('fr-FR')}</p>
                    )}
                  </div>
                </div>
              ) : (
              <>
              <div className="px-4 pb-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-slate-500 w-24">Centre:</span>
                  <select
                    value={stagiaire.centre_formation || ''}
                    onChange={(e) => handleUpdateStatut(stagiaire.user_id, 'centre_formation', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded-md"
                    data-testid={`select-centre-${stagiaire.user_id}`}
                  >
                    <option value="Atlantique">Atlantique</option>
                    <option value="Occitanie">Occitanie</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <UserPlus className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500 w-20">Tuteur:</span>
                  <select
                    value={stagiaire.tuteur_id || ''}
                    onChange={(e) => handleAttribuerTuteur(stagiaire.user_id, e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded-md"
                    data-testid={`select-tuteur-${stagiaire.user_id}`}
                  >
                    <option value="">Non attribué</option>
                    {tuteurs.map(t => (
                      <option key={t.user_id} value={t.user_id}>
                        {t.prenom} {t.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">Entreprise:</span>
                  <select
                    value={stagiaire.entreprise_id || ''}
                    onChange={(e) => handleAttribuerEntreprise(stagiaire.user_id, e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded-md"
                    data-testid={`select-entreprise-${stagiaire.user_id}`}
                  >
                    <option value="">Non attribuée</option>
                    {entreprises.map(e => {
                      const baseName = e.nom_entreprise || `${e.prenom || ''} ${e.nom || ''}`.trim() || 'Entreprise';
                      const ownerLabel = e.is_secondary_structure && e.owner_info
                        ? ` — ${e.owner_info.prenom || ''} ${e.owner_info.nom || ''}`.trimEnd()
                        : '';
                      return (
                        <option key={e.user_id} value={e.user_id}>
                          {baseName}{ownerLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Quick Info Bar - Ligne 2 : Tuteur 2 / Entreprise 2 */}
              <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div></div>
                <div className="flex items-center space-x-2">
                  <UserPlus className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500 w-20">Tuteur 2:</span>
                  <select
                    value={stagiaire.tuteur_id_2 || ''}
                    onChange={(e) => handleAttribuerTuteur2(stagiaire.user_id, e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded-md"
                    data-testid={`select-tuteur-2-${stagiaire.user_id}`}
                  >
                    <option value="">Non attribué</option>
                    {tuteurs.map(t => (
                      <option key={t.user_id} value={t.user_id}>
                        {t.prenom} {t.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">Entreprise 2:</span>
                  <select
                    value={stagiaire.entreprise_id_2 || ''}
                    onChange={(e) => handleAttribuerEntreprise2(stagiaire.user_id, e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded-md"
                    data-testid={`select-entreprise-2-${stagiaire.user_id}`}
                  >
                    <option value="">Non attribuée</option>
                    {entreprises.map(e => {
                      const baseName = e.nom_entreprise || `${e.prenom || ''} ${e.nom || ''}`.trim() || 'Entreprise';
                      const ownerLabel = e.is_secondary_structure && e.owner_info
                        ? ` — ${e.owner_info.prenom || ''} ${e.owner_info.nom || ''}`.trimEnd()
                        : '';
                      return (
                        <option key={e.user_id} value={e.user_id}>
                          {baseName}{ownerLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              </>
              )}

              {/* Expanded Details */}
              {expandedStagiaire === stagiaire.user_id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Info personnelles */}
                    <div>
                      <h4 className="font-semibold text-[#0F172A] mb-3">Informations</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-slate-500">Téléphone:</span> {stagiaire.user_info?.telephone || '-'}</p>
                        <p><span className="text-slate-500">Date de naissance:</span> {stagiaire.date_naissance || '-'}</p>
                        <p><span className="text-slate-500">Groupe sanguin:</span> {stagiaire.groupe_sanguin || '-'}</p>
                        <p><span className="text-slate-500">Contact urgence:</span> {stagiaire.contact_urgence_nom || '-'} {stagiaire.contact_urgence_telephone || ''}</p>
                      </div>
                      {stagiaire.tuteur_info && (
                        <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-xs text-slate-500 mb-1">Tuteur assigné</p>
                          <p className="font-medium">{stagiaire.tuteur_info.prenom} {stagiaire.tuteur_info.nom}</p>
                        </div>
                      )}
                      {stagiaire.entreprise_info && (
                        <div className="mt-2 p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-xs text-slate-500 mb-1">Entreprise assignée</p>
                          <p className="font-medium">{stagiaire.entreprise_info.nom || stagiaire.entreprise_info.prenom}</p>
                        </div>
                      )}
                      {stagiaire.tuteur_info_2 && (
                        <div className="mt-2 p-3 bg-white rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-500 mb-1">Tuteur 2 assigné</p>
                          <p className="font-medium">{stagiaire.tuteur_info_2.prenom} {stagiaire.tuteur_info_2.nom}</p>
                        </div>
                      )}
                      {stagiaire.entreprise_info_2 && (
                        <div className="mt-2 p-3 bg-white rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-500 mb-1">Entreprise 2 assignée</p>
                          <p className="font-medium">{stagiaire.entreprise_info_2.nom || stagiaire.entreprise_info_2.prenom}</p>
                        </div>
                      )}
                    </div>

                    {/* Documents */}
                    <div>
                      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Documents uploadés
                      </h4>
                      <div className="space-y-2">
                        {stagiaireDocuments[stagiaire.user_id]?.length > 0 ? (
                          stagiaireDocuments[stagiaire.user_id].map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
                              <span className="text-sm">{doc.document_type}</span>
                              <a href={doc.file_url || '#'} target="_blank" rel="noopener noreferrer" className="text-xs text-[#8DC63F] hover:underline">
                                Voir
                              </a>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500">Aucun document uploadé</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-slate-200 flex space-x-3">
                    <a href={`/progression?stagiaire=${stagiaire.user_id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        Voir progression
                      </Button>
                    </a>
                    <a href={`/recapitulatif?stagiaire=${stagiaire.user_id}`}>
                      <Button variant="outline" size="sm">
                        Récapitulatif complet
                      </Button>
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GestionStagiaires;
